import { netService } from './NetService';
import { dungeonService } from './DungeonService';
import { mapService } from './MapService';
import { saveService } from './SaveService';
import { audioService } from './AudioService';
import { hubService } from './HubService';
import { recoveryService } from './RecoveryService';
import { SocketService } from './SocketService';
import { coopDeathHost } from './CoopDeathHost';
import { resetCoopDeath } from './CoopDeathClient';
import { multiplayerService } from './MultiplayerService';
import { closeInterface } from './InterfaceService';
import { useGameStore } from '../store/gameSlice';
import { useNetSessionStore } from '../store/netSessionSlice';
import { LEAVE_RUN, makeRunWipe } from '../net/sessionEvents';
import { clearCoopResume, clearCoopGround } from '../net/reconnect';

// Delay before tearing down the client transport on a forfeit, so the reliable
// LEAVE_RUN message flushes before pc.close() drops it.
const LEAVE_RUN_FLUSH_MS = 200;

/**
 * Deliberate run exits (Escape → "Abandonner la run" / "Quitter le jeu"). The
 * confirmation popup is the EscapeMenu's responsibility; this service only runs
 * the role-aware exit. Solo paths are unchanged; a co-op host abandons via the
 * shared run-wipe path; a co-op client forfeits via the leave-run message.
 */
class RunExitService {
  /** Escape → "Abandonner la run" (dungeon only). */
  abandonRun(): void {
    closeInterface();
    if (netService.isHost()) {
      coopDeathHost.wipeRun('abandon');
      return;
    }
    if (netService.isClient()) {
      this.clientForfeit('hub');
      return;
    }
    dungeonService.emitGameEvent('defeat');
  }

  /** Escape → "Quitter le jeu". */
  quitGame(): void {
    closeInterface();
    if (netService.isHost()) {
      netService.session?.broadcast('reliable', makeRunWipe('abandon'));
      this.localRunCleanup(true);
      // Navigate to the menu (unmounts the dungeon) BEFORE leave() resets the
      // session: leave() bumps the epoch, which would otherwise re-run the
      // dungeon's network effect cleanup while still in the 'dungeon' scene.
      this.toMainMenu();
      multiplayerService.leave();
      return;
    }
    if (netService.isClient()) {
      this.clientForfeit('menu');
      return;
    }
    saveService.save();
    this.toMainMenu();
  }

  /** Co-op client: tell the host, recover locally, leave the room, navigate. */
  private clientForfeit(dest: 'hub' | 'menu'): void {
    netService.session?.broadcast('reliable', LEAVE_RUN);
    clearCoopResume();
    clearCoopGround();
    this.localRunCleanup(false);
    if (dest === 'hub') {
      void hubService.enterHub('from-defeat');
    } else {
      this.toMainMenu();
    }
    // Defer the transport teardown so the reliable LEAVE_RUN actually flushes:
    // an immediate netService.reset() closes the peer connection and can drop the
    // in-flight message, leaving a held ghost avatar host-side that blocks TPK.
    window.setTimeout(() => {
      netService.reset();
      useNetSessionStore.getState().clearSession();
    }, LEAVE_RUN_FLUSH_MS);
  }

  private localRunCleanup(clearSave: boolean): void {
    resetCoopDeath();
    SocketService.clearRunModifiers();
    recoveryService.applyDefeatRecovery();
    if (clearSave) {
      saveService.clearRunSave();
    }
  }

  private toMainMenu(): void {
    dungeonService.reset();
    mapService.reset();
    audioService.fadeOutMusic();
    useGameStore.getState().setGameplay('main-menu');
  }
}

export const runExitService = new RunExitService();
